<template>
  <DropdownContainer
    ref="containerRef"
    v-model:open="isOpen"
    :size
    :dropdownType
    :readonly
    :disabled="!lignesAAfficher.length || disabled"
    :alertLabel
    :alertType
    :placeholder
    :selectedLabel="resume"
    :deletable
    :filtered-items-count="lignesAAfficher.length"
    :read-only-reason
    dropdownMode="multiple"
    @deleting="selection = []"
    @on-key-down="onKeyDown"
  >
    <template #dropdown-items>
      <BasicDropdownItem
        v-for="(ligne, index) in lignesAAfficher"
        :key="index"
        :id="makeId(index)"
        :label="estMultiNiveau ? '' : ligne.label"
        :size
        :active="ligne.estGroupe ? ligne.toutSelectionne : ligne.estSelectionne"
        :highlighted="selectIndex === index"
        :class="estMultiNiveau ? ['ligne-niveau-' + ligne.niveau, { 'ligne-groupe': ligne.estGroupe }] : []"
        @click.stop="onItemClick(ligne)"
      >
        <BasicCheckbox
          v-if="estMultiNiveau"
          :model-value="ligne.estGroupe ? ligne.toutSelectionne : ligne.estSelectionne"
          :label="ligne.label"
          :class="{ 'checkbox-groupe': ligne.estGroupe }"
        />
      </BasicDropdownItem>
    </template>
  </DropdownContainer>
</template>

<script setup lang="ts" generic="T extends DropdownMultipleItem = DropdownMultipleItem">
  import { ref } from 'vue'
  import type { DropdownMultipleItem } from './BasicDropdownMultiple.types'
  import type { DropdownId, DropdownSize, DropdownType } from '../dropdown'
  import { useDropdownMultiple } from './useDropdownMultiple'
  import { useDropdownMultipleNavigation } from './useDropdownMultipleNavigation'
  import type { AlertType } from '../alert'
  import DropdownContainer from '@designSystem/components/wrapper/dropdownContainer/DropdownContainer.vue'

  const props = withDefaults(
    defineProps<{
      items?: T[]
      keyId?: (keyof T)[]
      keyLabel?: (keyof T)[]
      placeholder?: string
      size?: DropdownSize
      disabled?: boolean
      readonly?: boolean
      dropdownType?: DropdownType
      deletable?: boolean
      alertType?: AlertType
      alertLabel?: string | null
      itemId?: (item: T) => DropdownId
      readOnlyReason?: string
    }>(),
    { placeholder: 'Selectionnez', size: 'medium', dropdownType: 'form', deletable: true },
  )

  const selection = defineModel<DropdownId[]>({ default: () => [] })

  const containerRef = ref<typeof DropdownContainer | null>(null)

  const { isOpen, estMultiNiveau, lignesAAfficher, resume, onItemClick } = useDropdownMultiple<T>({
    items: () => props.items ?? [],
    selection,
    keyId: () => props.keyId,
    keyLabel: () => props.keyLabel,
    itemId: () => props.itemId,
  })

  const { selectIndex, makeId, onKeyDown } = useDropdownMultipleNavigation(lignesAAfficher, isOpen, onItemClick)
</script>

<style lang="less" scoped>
  // Separateur visuel pour les groupes et les items de niveau 0 sans sous-niveau
  .ligne-groupe,
  .ligne-niveau-0:not(.ligne-groupe) {
    border-top: 1px solid @neutral-200;
    padding-top: 4px;

    &:first-child {
      border-top: none;
      padding-top: 0;
    }
  }

  .checkbox-groupe :deep(.checkbox__label) {
    font-weight: @font-weight-semibold;
  }

  // Indentation et couleur de plus en plus claire par niveau
  .ligne-niveau-0 :deep(.checkbox) {
    margin-left: 0;
  }
  .ligne-niveau-1 :deep(.checkbox) {
    margin-left: 24px;
  }
  .ligne-niveau-2 :deep(.checkbox) {
    margin-left: 48px;
  }
  .ligne-niveau-3 :deep(.checkbox) {
    margin-left: 72px;
  }
  .ligne-niveau-4 :deep(.checkbox) {
    margin-left: 96px;
  }

  // Taille et padding reduits pour les sous-items (niveaux > 0)
  .ligne-niveau-1:not(.ligne-groupe),
  .ligne-niveau-2:not(.ligne-groupe),
  .ligne-niveau-3:not(.ligne-groupe),
  .ligne-niveau-4:not(.ligne-groupe) {
    padding: @spacing-1 @spacing-10;

    :deep(.checkbox__label) {
      font-size: @font-size-body-s;
    }
  }

  .ligne-niveau-1:not(.ligne-groupe):not(.dropdown-item--active) :deep(.checkbox__label) {
    font-weight: @font-weight-regular;
    color: @secondary-700;
  }
  .ligne-niveau-2:not(.ligne-groupe):not(.dropdown-item--active) :deep(.checkbox__label) {
    font-weight: @font-weight-regular;
    color: @secondary-600;
  }
  .ligne-niveau-3:not(.ligne-groupe):not(.dropdown-item--active) :deep(.checkbox__label) {
    font-weight: @font-weight-regular;
    color: @secondary-500;
  }
  .ligne-niveau-4:not(.ligne-groupe):not(.dropdown-item--active) :deep(.checkbox__label) {
    font-weight: @font-weight-regular;
    color: @secondary-400;
  }

</style>

<!-- Styles non-scopés car le dropdown menu est téléporté vers body -->
<style lang="less">
  // Background des items selectionnes (de plus en plus clair par niveau)
  .ligne-niveau-0.dropdown-item--active {
    background-color: @secondary-800 !important;
  }
  .ligne-niveau-1.dropdown-item--active {
    background-color: @secondary-600 !important;
  }
  .ligne-niveau-2.dropdown-item--active {
    background-color: @secondary-500 !important;
  }
  .ligne-niveau-3.dropdown-item--active {
    background-color: @secondary-400 !important;
  }
  .ligne-niveau-4.dropdown-item--active {
    background-color: @secondary-300 !important;
  }

  .dropdown-item--active {
    .checkbox__label {
      color: @white !important;
    }
    .checkbox__icon svg rect {
      fill: @white;
    }
    .checkbox__icon svg path {
      fill: @secondary-800;
    }
    .checkbox__icon--unchecked {
      border-color: @white;
    }
  }
</style>
